import {ForeignKey, Model, isEmpty} from "weed-js";
import {DUTCHIE, METRC} from "../constants";


export class CanonicalModel extends Model {

    static namespace = "canonical";
    static isCanonical = true;

    static from(other) {
        if (!other) {
            console.error("Cannot convert null or undefined to CanonicalModel", other, this, this.constructor);
            console.trace()
            throw new Error("Cannot convert null or undefined to CanonicalModel");
        }
        const existing = this.byId.get(other.id);
        if (existing) return existing;
        const namespace = other?.constructor?.namespace || other?.namespace || other?.constructor?.integration || other?.integration || null;
        if (namespace === DUTCHIE) return this.fromDutchie(other);
        if (namespace === METRC) return this.fromMetrc(other);
        if (other.dutchie_account_id !== undefined) return this.fromDutchie(other);
        if (other.metrc_account_id !== undefined) return this.fromMetrc(other);
        if (other instanceof CanonicalModel) return other;

        console.error("Unknown integration namespace: ", other, other.constructor, this, other.constructor.namespace, other.namespace);
        throw new Error("Unknown integration namespace: constructor: " + other.constructor.namespace + " instance: " + other.namespace);
    }

    static fromDutchie(other) {
        throw new Error("fromDutchie not implemented for " + this.name);
    }

    static fromMetrc(other) {
        throw new Error("fromMetrc not implemented for " + this.name);
    }
}

export class CanonicalOrganization extends CanonicalModel {
    static columns = ["id", "name"]
    static pluralName = "organizations";

    static from(other) {
        if (this.byId.has(other.id)) {
            return this.byId.get(other.id);
        }
        return new CanonicalOrganization({
            id: other.id,
            name: other.name
        })
    }
}

export class CanonicalIntegrationAccount extends CanonicalModel {
    static columns = ["id", "organization_id", "integration", "name"]
    static pluralName = "accounts";

    static foreignKeys = {
        organization: new ForeignKey(CanonicalOrganization)
    }

    static fromDutchie = (other) => {
        if (this.byId.has(other.id)) {
            return this.byId.get(other.id);
        }


        const name = other.display_name || other.name;
        if (!name) {
            console.error("Dutchie account is missing a name", other);
            throw new Error("Dutchie account is missing a name");
        }

        return new CanonicalIntegrationAccount({
            id: other.id,
            organization_id: other.organization_id,
            integration: DUTCHIE,
            name: name
        });
    }

    static fromMetrc = (other) => {
        if (this.byId.has(other.id)) {
            return this.byId.get(other.id);
        }

        const name = other.display_name || other.name;
        if (!name) {
            console.error("Metrc account is missing a name", other);
            throw new Error("Metrc account is missing a name");
        }
        return new CanonicalIntegrationAccount({
            id: other.id,
            organization_id: other.organization_id,
            integration: METRC,
            name: name
        });
    }
}

export class CanonicalFacility extends CanonicalModel {
    static columns = ["id", "integration_account_id", "integration", "name", "license_number"]
    static pluralName = "facilities";

    static foreignKeys = {
        integration_account: new ForeignKey(CanonicalIntegrationAccount)
    }

    static fromDutchie = (other) => {
        if (this.byId.has(other.id)) {
            return this.byId.get(other.id);
        }
        const integrationAccount = other.dutchie_account ? CanonicalIntegrationAccount.from(other.dutchie_account) : undefined;
        const fac = new CanonicalFacility({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integrationAccount: integrationAccount,
            integration: DUTCHIE,
            name: other.display_name,
            license_number: other.license_number
        });
        return fac;
    }

    static fromMetrc = (other) => {
        if (this.byId.has(other.id)) {
            return this.byId.get(other.id);
        }
        const integrationAccount = other.metrc_account ? CanonicalIntegrationAccount.from(other.metrc_account) : undefined;
        const fac = new CanonicalFacility({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            integration_account: integrationAccount,
            name: other.name,
            license_number: other.license_number
        });
        return fac;
    }
}

export class CanonicalMasterCategory extends CanonicalModel {
    static columns = [
        "id", "integration_account_id", "integration", "name"
    ];
    static pluralName = "master_categories";

    static fromDutchie = (other) => {
        return new CanonicalMasterCategory({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            name: other.name
        });
    }

    static fromMetrc = (other) => {
        return new CanonicalMasterCategory({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            name: other.name
        });
    }
}

export class CanonicalCategory extends CanonicalModel {
    static columns = [
        "id", "integration_account_id", "integration", "name", "external_id", "master_category_id"
    ];
    static pluralName = "categories";

    static foreignKeys = {
        master_category: new ForeignKey(CanonicalMasterCategory)
    }

    static fromDutchie = (other) => {
        return new CanonicalCategory({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            name: other.name,
            external_id: other.external_id,
            master_category_id: other.master_category_id,
            master_category: other?.master_category ? CanonicalMasterCategory.from(other.master_category) : undefined
        });
    }

    static fromMetrc = (other) => {
        return new CanonicalCategory({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            name: other.name,
            external_id: other.external_id,
            master_category_id: other.type_id,
            master_category: other?.type ? CanonicalMasterCategory.from(other.type) : undefined
        });
    }
}

export class CanonicalBrand extends CanonicalModel {
    static columns = ["id", "integration_account_id", "integration", "name"];
    static pluralName = "brands";

    static fromDutchie = (other) => {
        return new CanonicalBrand({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            name: other.name
        });
    }

    static fromMetrc = (other) => {
        return new CanonicalBrand({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            name: other.name
        });
    }
}

export class CanonicalVendor extends CanonicalModel {
    static columns = ["id", "integration_account_id", "integration", "name"];
    static pluralName = "vendors";

    static fromDutchie = (other) => {
        return new CanonicalVendor({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            name: other.name
        });
    }

    static fromMetrc = (other) => {
        return new CanonicalVendor({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            name: other.name
        });
    }
}

export class CanonicalStrain extends CanonicalModel {
    static columns = ["id", "integration_account_id", "integration", "name", "type"]
    static pluralName = "strains";

    static fromDutchie = (other) => {
        return new CanonicalStrain({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            name: other.name,
            type: other.type
        });
    }

    static fromMetrc = (other) => {
        return new CanonicalStrain({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            name: other.name,
            type: other.type
        });
    }
}

export class CanonicalProducer extends CanonicalModel {

    static columns = ["id", "integration_account_id", "integration", "name"]
    static pluralName = "producers";

    static fromDutchie = (other) => {
        return new CanonicalProducer({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            name: other.name
        });
    }

    static fromMetrc = (other) => {
        return new CanonicalProducer({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            name: other.name
        });
    }
}

export class CanonicalProduct extends CanonicalModel {

    static columns = [
        "id", "integration_account_id", "integration", "category_id", "brand_id",
        "name", "price", "strain_id", "vendor_id", "unit_cost",
        "producer_id", "sku", "image_url"
    ]
    static pluralName = "products";

    static foreignKeys = {
        category: new ForeignKey(CanonicalCategory),
        brand: new ForeignKey(CanonicalBrand),
        vendor: new ForeignKey(CanonicalVendor),
        strain: new ForeignKey(CanonicalStrain),
        producer: new ForeignKey(CanonicalProducer)
    }

    static fromDutchie = (other) => {
        const category = other?.category ? CanonicalCategory.from(other.category) : undefined;
        const brand = other?.brand ? CanonicalBrand.from(other.brand) : undefined;
        const vendor = other?.vendor ? CanonicalVendor.from(other.vendor) : undefined;
        const strain = other?.strain ? CanonicalStrain.from(other.strain) : undefined;
        const producer = other?.producer ? CanonicalProducer.from(other.producer) : undefined;


        const product = new CanonicalProduct({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            category_id: other.category_id,
            category: category,
            brand_id: other.brand_id,
            brand: brand,
            name: other.name,
            price: other.price,
            strain_id: other.strain_id,
            strain: strain,
            vendor_id: other.vendor_id,
            vendor: vendor,
            unit_cost: other.unit_cost,
            producer_id: other.producer_id,
            producer: producer,
            sku: other.sku,
            image_url: other.image_url
        });

        return product;
    }

    static fromMetrc = (other) => {
        const inventoryItems = Array.from(other.inventory_items || []);
        const brand = inventoryItems.find(item => !isEmpty(item.brand_id));
        const strain = inventoryItems.find(item => !isEmpty(item.strain_id));
        const wholesalePrice = inventoryItems.find(item => {
            const wholesalePrices = Array.from(item.package?.package_wholesale_prices || [])
            return wholesalePrices.find(price => {
                return !isEmpty(price.receiver_wholesale_price) || !isEmpty(price.shipper_wholesale_price);
            });
        });
        const unitCost = wholesalePrice ? (wholesalePrice.receiver_wholesale_price || wholesalePrice.shipper_wholesale_price) : null;
        const packages = [];
        for (const inventoryItem of inventoryItems) {
            inventoryItem.package && packages.push(inventoryItem.package);
        }

        const transactions = Array.from(packages?.find(pkg => pkg.transactions && pkg.transactions?.size > 0)?.transactions || []);
        const price = transactions?.find(txn => !isNaN(txn.price))?.price || null;

        const findVendorDeliveredPackage = (deliveredPackage) => {
            const transferShipper = deliveredPackage.delivery?.transfer?.shipper_facility_id;
            const deliveryShipper = deliveredPackage.delivery?.shipper_facility_id;
            return transferShipper || deliveryShipper;
        }

        const vendorPackage = packages.find(p => Array.from(p.delivered_packages || []).find(findVendorDeliveredPackage));
        const deliveredPackages = vendorPackage ? Array.from(vendorPackage.delivered_packages || []) : [];
        const vendorDeliveredPackage = deliveredPackages.find(findVendorDeliveredPackage);
        const vendorId = vendorDeliveredPackage ? (vendorDeliveredPackage.delivery?.transfer?.shipper_facility_id || vendorDeliveredPackage.delivery?.shipper_facility_id) : null;

        const product = new CanonicalProduct({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            category_id: other?.category_id,
            category: other?.category ? CanonicalCategory.from(other.category) : undefined,
            brand_id: brand?.id,
            brand: brand ? CanonicalBrand.from(brand) : undefined,
            name: other?.name,
            price: price,
            strain_id: strain?.id,
            strain: strain ? CanonicalStrain.from(strain) : undefined,
            vendor_id: vendorId,
            unit_cost: unitCost,
            producer_id: null,
            sku: null,
            image_url: null
        });

        return product;
    }

}

export class CanonicalCustomer extends CanonicalModel {
    static columns = ["id", "integration_account_id", "integration", "date_of_birth", "gender", "type_name"]
    static pluralName = "customers";

    static fromDutchie = (other) => {
        return new CanonicalCustomer({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            date_of_birth: other.date_of_birth,
            gender: other.gender,
            type_name: other.type_name
        })
    }

    static fromMetrc = (other) => {
        return new CanonicalCustomer({
            id: null,
            integration_account_id: null,
            integration: METRC,
            date_of_birth: null,
            gender: null,
            type_name: null
        });
    }

    static foreignKeys = {
        integration_account: new ForeignKey(CanonicalIntegrationAccount)
    }
}

export class CanonicalEmployee extends CanonicalModel {
    static columns = ["id", "integration_account_id", "integration", "external_id"];
    static pluralName = "employees";

    static fromDutchie = (other) => {
        return new CanonicalEmployee({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            external_id: other.external_id
        });
    }

    static fromMetrc = (other) => {
        return new CanonicalEmployee({
            id: other?.id || null,
            integration_account_id: other?.metrc_account_id || null,
            integration: METRC,
            external_id: other?.name || null
        });
    }
}

export class CanonicalTransaction extends CanonicalModel {
    static columns = [
        "id", "integration_account_id", "integration", "transaction_date", "loyalty_earned",
        "loyalty_spent", "subtotal", "tax", "total", "total_discount",
        "total_items", "customer_id", "employee_id", "order_method",
        "order_source", "order_type", "transaction_type", "external_id", "facility_id",
    ]
    static pluralName = "transactions";

    static foreignKeys = {
        customer: new ForeignKey(CanonicalCustomer),
        employee: new ForeignKey(CanonicalEmployee),
        facility: new ForeignKey(CanonicalFacility)
    }

    static fromDutchie = (other) => {
        const txn = new CanonicalTransaction({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            facility_id: other.dutchie_account_id,
            facility: other?.dutchie_account ? CanonicalFacility.from(other.dutchie_account) : undefined,
            transaction_date: other.transaction_date,
            loyalty_earned: other.loyalty_earned,
            loyalty_spent: other.loyalty_spent,
            subtotal: other.subtotal,
            tax: other.tax,
            total: other.total,
            total_discount: other.total_discount,
            total_items: other.total_items,
            customer_id: other.customer_id,
            customer: other.customer ? CanonicalCustomer.from(other.customer) : undefined,
            employee_id: other.employee_id,
            employee: other.employee ? CanonicalEmployee.from(other.employee) : undefined,
            order_method: other.order_method,
            order_source: other.order_source,
            order_type: other.order_type,
            transaction_type: other.transaction_type,
            external_id: other.external_id
        });

        return txn;
    }

    static fromMetrc = (other) => {
        const totalItems = other.quantity_sold;
        const total = other.total_price || other.price;
        const subtotal = other.sub_total; // Metrc does not provide a separate subtotal
        const tax = other.tax; // Metrc does not provide tax information
        const totalDiscount = other.discount_amount; // Metrc does not provide discount information
        const receipt = other.receipt;
        if (!receipt) {
            throw new Error("No receipt found for Metrc transaction with id " + other.id);
        }
        const transactionDate = new Date(receipt?.sales_date_time);
        if (isNaN(transactionDate.getTime())) {
            throw new Error("Invalid transaction date for Metrc transaction with id " + other.id + ": " + receipt?.sales_date_time);
        }
        const employee = receipt.recorded_by;
        const employeeId = employee ? employee.id : null;

        return new CanonicalTransaction({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            transaction_date: transactionDate,
            loyalty_earned: 0, // Metrc does not provide loyalty information
            loyalty_spent: 0, // Metrc does not provide loyalty information
            subtotal: subtotal,
            tax: tax,
            total: total,
            total_discount: totalDiscount,
            total_items: totalItems,
            customer_id: null, // Metrc does not provide customer information
            customer: null,
            facility_id: other?.facility_id || null,
            facility: other?.facility ? CanonicalFacility.from(other.facility) : undefined,
            employee_id: employeeId, // Metrc does not provide employee information
            employee: employee ? CanonicalEmployee.from(employee) : undefined,
            order_method: null, // Metrc does not provide order method information
            order_source: null, // Metrc does not provide order source information
            order_type: null, // Metrc does not provide order type information
            transaction_type: "sale", // Assuming all Metrc transactions are sales
            external_id: receipt?.external_id || null
        });
    }

}

export class CanonicalPackage extends CanonicalModel {
    static columns = ["id", "integration_account_id", "integration", "label", "product_id", "quantity"]
    static pluralName = "packages";
    static foreignKeys = {
        product: new ForeignKey(CanonicalProduct)
    }

    static fromDutchie = (other) => {
        return new CanonicalPackage({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration: DUTCHIE,
            label: other.label,
            quantity: other.quantity,
            product_id: other.product_id,
            product: other?.product ? CanonicalProduct.from(other.product) : undefined
        });
    }

    static fromMetrc = (other) => {
        const inventoryItems = Array.from(other.inventory_items || []);
        const inventoryItem = inventoryItems.find(item => item.quantity > 0) || inventoryItems[0] || null;

        return new CanonicalPackage({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            label: other.label,
            quantity: other.quantity,
            product_id: inventoryItem?.product_id || undefined,
            product: inventoryItem?.product ? CanonicalProduct.from(inventoryItem.product) : undefined
        });
    }
}

export class CanonicalDeliveredPackage extends CanonicalModel {
    static columns = ["id", "integration_account_id", "integration", "vendor_id", "package_id"];
    static pluralName = "delivered_packages";
    static foreignKeys = {
        vendor: new ForeignKey(CanonicalVendor),
        package: new ForeignKey(CanonicalPackage)
    }

    static fromDutchie = (other) => {
        console.trace()
        throw new Error("Not implemented: Dutchie does not have delivered packages");
    }

    static fromMetrc = (other) => {
        const canonicalPackage = other.package ? CanonicalPackage.from(other.package) : undefined;
        const vendorId = other.delivery?.transfer?.shipper_facility_id || other.delivery?.shipper_facility_id || null;
        const delivery = other.delivery;
        const transfer = delivery?.transfer;
        const metrcShipperFacility = transfer?.shipper_facility || delivery?.shipper_facility;
        const vendor = metrcShipperFacility ? CanonicalVendor.from(metrcShipperFacility) : undefined;

        return new CanonicalDeliveredPackage({
            id: other.id,
            integration_account_id: other.metrc_account_id,
            integration: METRC,
            vendor_id: vendorId,
            vendor: vendor,
            package_id: other.package_id,
            package: canonicalPackage,
        });
    }

}

export class CanonicalTransactionItem extends CanonicalModel {
    static columns = [
        "id", "integration_account_id", "integration", "batch_id", "package_id", "product_id",
        "transaction_id", "vendor_id", "quantity", "total_discount",
        "total_price", "unit_cost", "unit_price", "external_id"
    ];
    static pluralName = "items";

    static foreignKeys = {
        product: new ForeignKey(CanonicalProduct),
        transaction: new ForeignKey(CanonicalTransaction),
        integration_account: new ForeignKey(CanonicalIntegrationAccount),
        vendor: new ForeignKey(CanonicalVendor),
        package: new ForeignKey(CanonicalPackage),
    };

    static fromDutchie = (other) => {
        // Ideally we would never actually translate from
        return new CanonicalTransactionItem({
            id: other.id,
            integration_account_id: other.dutchie_account_id,
            integration_account: other?.dutchie_account ? CanonicalIntegrationAccount.from(other.dutchie_account) : undefined,
            integration: DUTCHIE,
            batch_id: other.batch_id,
            batch: null,
            package_id: other.package_id,
            package: other?.package ? CanonicalPackage.from(other.package) : undefined,
            product_id: other.product_id,
            product: other?.product ? CanonicalProduct.from(other.product) : undefined,
            transaction_id: other.transaction_id,
            transaction: other?.transaction ? CanonicalTransaction.from(other.transaction) : undefined,
            vendor_id: other.vendor_id,
            vendor: other?.vendor ? CanonicalVendor.from(other.vendor) : undefined,
            quantity: other.quantity,
            total_discount: other.total_discount,
            total_price: other.total_price,
            unit_cost: other.unit_cost,
            unit_price: other.unit_price,
            external_id: other.external_id
        });
    }

    static fromMetrc = (other) => {
        const receipt = other.transaction?.receipt;
        if (!receipt) {
            // console.error("No receipt found for Metrc transaction item", other, other.constructor, receipt);
            throw new Error("No receipt found for Metrc transaction item with id " + other.id, receipt);
        }
        const transaction = other.transaction;
        if (!transaction) {
            console.error("No transaction found for Metrc transaction item", other, other.constructor, receipt);
            throw new Error("No transaction found for Metrc transaction item with id " + other.id + " in receipt " + receipt.id);
        }
        const pkg = transaction?.package;
        const inventoryItems = Array.from(pkg?.inventory_items || []);
        const productInventoryItem = inventoryItems.find(item => !isEmpty(item.product_id));
        const inventoryItem = inventoryItems.find(item => {
            const wholesalePrices = Array.from(item.package?.package_wholesale_prices || [])
            return wholesalePrices.find(price => {
                return !isEmpty(price.receiver_wholesale_price) || !isEmpty(price.shipper_wholesale_price);
            });
        });
        const wholesalePrice = inventoryItem?.item?.package?.package_wholesale_prices.find(price => {
            return !isEmpty(price.receiver_wholesale_price) || !isEmpty(price.shipper_wholesale_price);
        });

        const unitCost = wholesalePrice ? (wholesalePrice.receiver_wholesale_price || wholesalePrice.shipper_wholesale_price) : null;
        const unitPrice = !isEmpty(transaction.total_price) && !isEmpty(transaction.quantity_sold)  && transaction.quantity_sold > 0 ? transaction.total_price / transaction.quantity_sold : null;

        if (!productInventoryItem?.product_id) {
            console.error("No product found for Metrc transaction item from any package in inventory items", inventoryItems);
            throw new Error("No product found for Metrc transaction item with id " + other.id + " in package " + pkg);
        }

        const canonicalProduct = productInventoryItem?.product ? CanonicalProduct.from(productInventoryItem?.product) : undefined;
        const vendorId = canonicalProduct?.vendor_id || null;

        return new CanonicalTransactionItem({
            id: other.id,
            integration_account_id: receipt.metrc_account_id,
            integration_account: receipt?.metrc_account ? CanonicalIntegrationAccount.from(receipt.metrc_account) : undefined,
            integration: METRC,
            batch_id: null,
            batch: null,
            package_id: pkg?.id || null,
            package: pkg ? CanonicalPackage.from(pkg) : undefined,
            product_id: productInventoryItem?.product_id,
            product: productInventoryItem?.product ? CanonicalProduct.from(productInventoryItem?.product) : undefined,
            transaction_id: transaction.id,
            transaction: transaction ? CanonicalTransaction.from(transaction) : undefined,
            vendor_id: vendorId,
            vendor: undefined,
            quantity: transaction?.quantity_sold || null,
            total_discount: transaction?.discount_amount || null,
            total_price: transaction?.total_price || null,
            unit_cost: unitCost,
            unit_price: unitPrice,
            external_id: receipt.id + "." + other.id
        });
    }

    toNumber(value) {
        if (value === null || value === undefined) {
            return value;
        }
        const num = parseFloat(value);
        if (isNaN(num)) {
            throw new Error(`Value ${value} is not a valid number for Item with id ${this.id}`);
        }
        return num;
    }

    toRow() {
        if (isEmpty(this.product)) {
            throw new Error("Product with id " + this.product_id + " was not found for Item with id " + this.id);
        }
        if (isEmpty(this.transaction)) {
            throw new Error("Transaction with id " + this.transaction_id + " was not found for Item with id " + this.id);
        }
        if (isEmpty(this.integration_account)) {
            throw new Error("Integration Account with id " + this.integration_account_id  + " was not found for Item with id " + this.id);
        }

        const txnDate = this.transaction.transaction_date;
        const inventory = isEmpty(this.product.inventory) ? {} : Array.from(this.product.inventory).find(inv => inv.package_id === this.package_id);
        const isDeleted = isEmpty(inventory) ? true : inventory.deleted;
        const organizationId = this.integration_account.organization_id;
        const inventoryUnitCost = isDeleted ? 0 : inventory.unit_cost;
        const inventoryUnitPrice = isDeleted ? 0 : inventory.unit_price;
        const unitCost = this.toNumber(isEmpty(this.unit_cost) ? inventoryUnitCost : this.unit_cost);
        const unitPrice = this.toNumber(this.unit_price === null ? inventoryUnitPrice : this.unit_price) || this.product?.price || 0;
        const quantityAvailable = inventory?.quantity_available || this.package?.quantity || 0;

        const employeeId = this.transaction?.employee_id;
        const employeeExternalId = isEmpty(this.transaction?.employee) ? null : this.transaction.employee.external_id;
        const quantitySold = this.toNumber(this.quantity);
        if (employeeId && isEmpty(employeeExternalId)) {
            console.warn(`Employee with id ${employeeId} does not have an external_id set. This may cause issues with reporting.`);
        }

        if (txnDate?.getTime && isNaN(txnDate.getTime())) {
            throw new Error("Invalid transaction date for Item with id " + this.id + ": "
                + this.transaction.transaction_date + " (parsed as " + txnDate + ")");
        }

        // package/delivered_packages/vendor/name
        const pkg = this.package;
        const deliveredPackages = Array.from(pkg?.delivered_packages || []);
        const deliveredPackage = deliveredPackages.length > 0 ? deliveredPackages.find(dp => !isEmpty(dp?.vendor_id)) : null;
        if (this.product?.vendor_id || deliveredPackage?.vendor_id) {
            const vendorId = this.product?.vendor_id || deliveredPackage?.vendor_id;
            if (this.vendor_id !== vendorId) {
                console.warn(`Vendor ID mismatch for Item ${this.id}: item.vendor_id=${this.vendor_id} but product.vendor_id or deliveredPackage.vendor_id=${vendorId}`);
            }
        }

        return [
            this.transaction.external_id,
            this.external_id,
            this.transaction?.employee?.external_id ? this.transaction.employee.external_id : "---",
            isEmpty(this.transaction?.facility) ? "" : this.transaction?.facility.name,
            isEmpty(this.product.category) || isEmpty(this.product.category.master_category) ? "---" : this.product.category.master_category.name,
            isEmpty(this.product.category) ? "---" : this.product.category.name,
            isEmpty(this.product.brand) ? "---" : this.product.brand.name,
            isEmpty(this.product.vendor) ? "---" : this.product.vendor.name,
            this.product_id > -1 ? {
                props: {
                    href: `/analytics/organizations/${organizationId}/integration/${this.integration}/product/${this.product_id}`,
                    role: "link"
                },
                displayValue: this.product.name,
            } : null,
            this.product.sku,
            quantityAvailable,
            isNaN(quantitySold) ? null : quantitySold,
            !isNaN(unitPrice * quantitySold) ? unitPrice * quantitySold : null,
            unitCost === 0 || isNaN(unitCost) ? null : unitCost * quantitySold,
            unitCost === 0 || unitPrice === 0 || isNaN(unitCost * unitPrice * quantitySold) ? null : (unitPrice - unitCost) * quantitySold,
            isNaN(unitPrice) ? null : unitPrice,
            isNaN(unitCost) ? null : unitCost,
            txnDate
        ];
    }
}

export class CanonicalInventory extends CanonicalModel {
    static columns = ["id", "description", "expiration_datetime", "image_url",
        "quantity_available", "unit_cost", "unit_price", "batch_id",
        "brand_id", "integration_account_id", "integration", "package_id", "product_id",
        "deleted"]

    static pluralName = "inventory";

    static foreignKeys = {
        brand: new ForeignKey(CanonicalBrand),
        integration_account: new ForeignKey(CanonicalIntegrationAccount),
        package: new ForeignKey(CanonicalPackage),
        product: new ForeignKey(CanonicalProduct),
    }

    static fromDutchie = (other) => {
        console.log("Quantity available: ", other.quantity_available, "other", other);
        return new CanonicalInventory({
            id: other.id,
            description: other.description,
            expiration_datetime: other.expiration_datetime,
            image_url: other.image_url,
            quantity_available: other.quantity_available,
            unit_cost: other.unit_cost,
            unit_price: other.unit_price,
            batch_id: other.batch_id,
            batch: null,
            brand_id: other.brand_id,
            brand: other?.brand ? CanonicalBrand.from(other.brand) : undefined,
            integration_account_id: other.dutchie_account_id,
            integration_account: other?.dutchie_account ? CanonicalIntegrationAccount.from(other.dutchie_account) : undefined,
            package_id: other.package_id,
            package: other?.package ? CanonicalPackage.from(other.package) : undefined,
            product_id: other.product_id,
            product: other?.product ? CanonicalProduct.from(other.product) : undefined,
            deleted: other.deleted
        });
    }

    static fromMetrc = (other) => {

        const brand = Array.from(other.package?.inventory_items || []).find(item => !isEmpty(item?.product?.brand_id))?.brand || undefined;

        const wholesalePrices = Array.from(other.package?.package_wholesale_prices || []);
        const wholesalePrice = wholesalePrices.find(price => {
            return !isEmpty(price.receiver_wholesale_price) || !isEmpty(price.shipper_wholesale_price);
        });

        const unitCost = wholesalePrice ? (wholesalePrice.receiver_wholesale_price || wholesalePrice.shipper_wholesale_price) : null;
        const packages = Array.from(other.packages || []);
        const pkg = packages.find(pkg => pkg.quantity > 0);
        const unitPrice = pkg ? pkg.price : null;
        const expirationDate = pkg?.expiration_date || pkg?.sell_by_date || pkg?.use_by_date || null;

        return new CanonicalInventory({
            id: other.id,
            description: other.description || null,
            expiration_datetime: expirationDate,
            image_url: null,
            quantity_available: pkg ? pkg.quantity : 0,
            unit_cost: unitCost,
            unit_price: unitPrice,
            batch_id: null,
            brand_id: brand?.id,
            brand: brand ? CanonicalBrand.from(brand) : undefined,
            integration_account_id: other.metrc_account_id,
            integration_account: other.metrc_account ? CanonicalIntegrationAccount.from(other.metrc_account) : undefined,
            package_id: other.package_id,
            package: pkg ? CanonicalPackage.from(pkg) : undefined,
            product_id: other.product_id,
            product: other?.product ? CanonicalProduct.from(other.product) : undefined,
            deleted: (pkg?.quantity || 0) > 0 && (!expirationDate || new Date(expirationDate) < new Date())
        });
    }
}
